import { useState, useEffect, lazy, Suspense } from 'react'
import { Web3Provider } from './contexts/Web3Context'
import { ToastContainer } from './components/common/Toast'
import AppHeader from './components/layout/AppHeader'
import WrongNetworkBanner from './components/common/WrongNetworkBanner'
import Disclaimer from './components/common/Disclaimer'
import SoundToggle from './components/common/SoundToggle'
import ErrorBoundary from './components/common/ErrorBoundary'
import { cyberpunkTheme } from './styles/theme'

const Home = lazy(() => import('./pages/Home'))
const Rooms = lazy(() => import('./pages/Rooms'))
const GameRoom = lazy(() => import('./pages/GameRoom'))
const Profile = lazy(() => import('./pages/Profile'))
const Whitepaper = lazy(() => import('./pages/Whitepaper'))

const t = cyberpunkTheme.colors

const PageFallback = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.text.secondary }}>
    <span>Loading…</span>
  </div>
)

const fallbackPageStyle = {
  minHeight: '100vh',
  background: t.bg.darkest,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  flexDirection: 'column',
  gap: '1rem',
}

const fallbackButtonStyle = {
  padding: '0.75rem 2rem',
  background: cyberpunkTheme.colors.button.accent,
  border: `2px solid ${t.primary}`,
  borderRadius: '4px',
  color: t.bg.darkest,
  cursor: 'pointer',
  fontFamily: cyberpunkTheme.fonts.display,
  fontSize: '0.5rem',
  letterSpacing: '0.05em',
  boxShadow: cyberpunkTheme.colors.glow.cyan,
}

function Router() {
  const [route, setRoute] = useState(window.location.hash.slice(1) || '/')

  useEffect(() => {
    const handleHashChange = () => setRoute(window.location.hash.slice(1) || '/')
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  return (
    <Suspense fallback={<PageFallback />}>
      {route === '/' && <Home />}
      {route === '/rooms' && <Rooms />}
      {route === '/profile' && <Profile />}
      {route === '/whitepaper' && <Whitepaper />}
      {(() => {
        const gameMatch = route.match(/^\/game\/(\d+)$/)
        if (gameMatch) {
          const roomId = parseInt(gameMatch[1], 10)
          if (!Number.isInteger(roomId) || roomId < 0) {
            return (
              <div style={fallbackPageStyle}>
                <h2>Invalid room</h2>
                <button onClick={() => { window.location.hash = '#/' }} style={fallbackButtonStyle}>
                  GO HOME
                </button>
              </div>
            )
          }
          return <GameRoom roomId={roomId} />
        }
        if (route !== '/' && route !== '/rooms' && route !== '/profile' && route !== '/whitepaper') {
          return (
            <div style={fallbackPageStyle}>
              <h2>Page not found</h2>
              <button onClick={() => { window.location.hash = '#/' }} style={fallbackButtonStyle}>
                GO HOME
              </button>
            </div>
          )
        }
        return null
      })()}
    </Suspense>
  )
}

export default function App() {
  return (
    <Web3Provider>
      <AppHeader />
      <WrongNetworkBanner />
      <ErrorBoundary>
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1 }}><Router /></div>
          <Disclaimer />
        </div>
      </ErrorBoundary>
      <ToastContainer />
      <SoundToggle />
    </Web3Provider>
  )
}
